// src/ArchiveModal.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Tabs,
  Tab,
  Box,
  Typography,
  DialogActions,
  Button,
  List,
  ListItem,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import api from './apiClient';

function ArchiveModal({ open, onClose, workspaceId, userId, onTasksRestored }) {
  const { enqueueSnackbar } = useSnackbar();
  const [tab, setTab] = useState(0);
  const [archivedItems, setArchivedItems] = useState({
    tasks: [],
    projects: [],
    members: [],
    compliance: [],
  });
  const [deletedTasks, setDeletedTasks] = useState([]);

  // Common loader for archived + deleted items
  const loadArchived = async () => {
    if (!workspaceId) return;

    try {
      const res = await api.get('/api/archived-items', {
        params: { workspace_id: workspaceId },
      });

      const items = Array.isArray(res.data) ? res.data : [];

      setArchivedItems({
        tasks: items.filter((i) => i.type === 'task'),
        projects: items.filter((i) => i.type === 'project'),
        members: items.filter((i) => i.type === 'member'),
        compliance: items.filter((i) => i.type === 'compliance'),
      });

      // Fetch deleted tasks
      const deletedRes = await api.get('/api/deleted-tasks', {
        params: { workspace_id: workspaceId },
      });
      setDeletedTasks(Array.isArray(deletedRes.data) ? deletedRes.data : []);
    } catch (err) {
      console.error('Error fetching archived items:', err);
      enqueueSnackbar('Failed to load archived items', { variant: 'error' });
    }
  };

  // Restore a single deleted task
  const restoreTask = async (taskId) => {
    try {
      await api.post('/api/tasks/restore', {
        task_id: taskId,
        user_id: userId,
      });

      enqueueSnackbar('Task restored', { variant: 'success' });

      // Refresh archived + deleted lists
      await loadArchived();

      // Optionally notify parent to refresh main task list
      if (typeof onTasksRestored === 'function') {
        onTasksRestored();
      }
    } catch (err) {
      console.error('Failed to restore task:', err);
      enqueueSnackbar('Failed to restore task', { variant: 'error' });
    }
  };

  useEffect(() => {
    if (open) {
      loadArchived();
    }
  }, [open, workspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>Archived Items</DialogTitle>

      <Tabs value={tab} onChange={(_, v) => setTab(v)}>
        <Tab label="Tasks" />
        <Tab label="Projects" />
        <Tab label="Members" />
        <Tab label="Compliance" />
        <Tab label="Deleted Tasks" />
      </Tabs>

      <DialogContent>
        <Box>
          {/* Archived Tasks */}
          {tab === 0 && (
            <Box>
              <Typography>Archived Tasks: {archivedItems.tasks.length}</Typography>
              <List>
                {archivedItems.tasks.map((item) => (
                  <ListItem key={item.id}>
                    {item.data && item.data.title ? item.data.title : 'Untitled Task'}
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Archived Projects */}
          {tab === 1 && (
            <Box>
              <Typography>Archived Projects: {archivedItems.projects.length}</Typography>
              <List>
                {archivedItems.projects.map((item) => (
                  <ListItem key={item.id}>
                    {item.data && item.data.name ? item.data.name : 'Untitled Project'}
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Archived Members (V2) */}
          {tab === 2 && (
            <Box>
              <Typography>Archived Members: {archivedItems.members.length}</Typography>
              {/* Add detailed list in V2 */}
            </Box>
          )}

          {/* Archived Compliance (V2) */}
          {tab === 3 && (
            <Box>
              <Typography>Archived Compliance: {archivedItems.compliance.length}</Typography>
              {/* Add detailed list in V2 */}
            </Box>
          )}

          {/* Deleted Tasks with Restore button */}
          {tab === 4 && (
            <Box>
              <Typography>Deleted Tasks: {deletedTasks.length}</Typography>
              <List>
                {deletedTasks.map((t) => (
                  <ListItem key={t.id}>
                    <span style={{ flex: 1 }}>{t.title || 'Untitled Task'}</span>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => restoreTask(t.id)}
                    >
                      Restore
                    </Button>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={async () => {
            try {
              if (window.confirm('Restore workspace?')) {
                await api.post(`/api/workplaces/${workspaceId}/restore`, {
                  user_id: userId,
                });
                enqueueSnackbar('Workspace restored (V2 full items)', {
                  variant: 'success',
                });
                await loadArchived();
              }
            } catch (err) {
              console.error('Failed to restore workspace:', err);
              enqueueSnackbar('Failed to restore workspace', { variant: 'error' });
            }
          }}
        >
          Restore
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default ArchiveModal;
